import { Link } from "react-router-dom";
import { Meta } from "../components/Meta";
import { routes } from "../config/routes";
import { siteConfig } from "../config/site";

interface PageHeroProps {
  eyebrow: string;
  title: string;
  lead: string;
}

function PageHero({ eyebrow, title, lead }: PageHeroProps) {
  return (
    <header className="page-hero">
      <div className="page-hero-inner">
        <p className="eyebrow">{eyebrow}</p>
        <h1>{title}</h1>
        <p className="lead">{lead}</p>
      </div>
    </header>
  );
}

export function AboutPage() {
  return (
    <main id="main">
      <Meta
        title="About | Beautify Ourselves"
        description="Learn about the clear, considered beauty shopping experience Beautify Ourselves is creating."
        path={routes.about}
      />
      <PageHero
        eyebrow="About us"
        title="Beauty shopping, made more human."
        lead="Beautify Ourselves is being built around a simple idea: present beauty products clearly, carefully, and without inflated promises."
      />
      <article className="content-page">
        <h2>A considered beauty collection</h2>
        <p>
          Beautify Ourselves brings together beauty essentials in a setting that feels warm,
          calm and easy to explore. Our first collection will span cleansers, moisturisers and
          body care.
        </p>
        <p>
          We believe useful information matters. Product pages will pair a distinctive point
          of view with clear descriptions, ingredients and practical guidance—without
          unnecessary noise or exaggerated promises.
        </p>
        <h2>What matters to us</h2>
        <div className="values-grid">
          <div>
            <h3>Clarity</h3>
            <p>Product details written to be useful, readable and easy to understand.</p>
          </div>
          <div>
            <h3>Consideration</h3>
            <p>A focused collection presented with care rather than an overwhelming catalogue.</p>
          </div>
          <div>
            <h3>Confidence</h3>
            <p>Straightforward availability, delivery and customer-care information.</p>
          </div>
        </div>
      </article>
    </main>
  );
}

export function DeliveryReturnsPage() {
  return (
    <main id="main">
      <Meta
        title="Delivery & Returns | Beautify Ourselves"
        description="UK and Republic of Ireland delivery information for Beautify Ourselves, with returns guidance to follow before ordering opens."
        path={routes.delivery}
      />
      <PageHero
        eyebrow="Delivery & Returns"
        title="Delivery, made clear."
        lead="Straightforward delivery information for the UK and Republic of Ireland."
      />
      <article className="content-page">
        <h2>United Kingdom</h2>
        <dl className="definition-list">
          <div className="definition-row">
            <dt>Delivery estimate</dt>
            <dd>Estimated UK delivery: {siteConfig.delivery.uk.estimate}.</dd>
          </div>
          <div className="definition-row">
            <dt>Shipping information</dt>
            <dd>{siteConfig.delivery.uk.shipping}</dd>
          </div>
        </dl>
        <h2>Republic of Ireland</h2>
        <dl className="definition-list">
          <div className="definition-row">
            <dt>Delivery estimate</dt>
            <dd>{siteConfig.delivery.ireland.notice}</dd>
          </div>
          <div className="definition-row">
            <dt>Shipping information</dt>
            <dd>{siteConfig.delivery.ireland.shipping}</dd>
          </div>
        </dl>
        <p>
          Tracking information will be shared when it is available. If an order is delayed,
          we’ll let you know.
        </p>
        <h2>Returns</h2>
        <p>
          Returns information will be published here before ordering opens. No orders are
          currently being accepted.
        </p>
      </article>
    </main>
  );
}

export function FaqPage() {
  return (
    <main id="main">
      <Meta
        title="FAQ | Beautify Ourselves"
        description="Answers about the Beautify Ourselves collection, ordering, delivery, returns and customer care."
        path={routes.faq}
      />
      <PageHero
        eyebrow="Frequently asked questions"
        title="Clear answers, from the start."
        lead="Useful information about the collection and the shopping experience."
      />
      <div className="content-page">
        <details open>
          <summary>When can I order?</summary>
          <p>Orders are not currently available. The first collection is still being prepared.</p>
        </details>
        <details>
          <summary>Where will you deliver?</summary>
          <p>
            Delivery information is provided separately for the United Kingdom and Republic
            of Ireland. Available options will be shown before checkout when ordering opens.
          </p>
        </details>
        <details>
          <summary>How long will UK delivery take?</summary>
          <p>{siteConfig.delivery.uk.notice}</p>
        </details>
        <details>
          <summary>How long will Republic of Ireland delivery take?</summary>
          <p>{siteConfig.delivery.ireland.notice}</p>
        </details>
        <details>
          <summary>What is your returns policy?</summary>
          <p>Returns information will be published before ordering opens.</p>
        </details>
        <details>
          <summary>How can I contact Beautify Ourselves?</summary>
          <p>Customer care details will be published when the collection opens for ordering.</p>
        </details>
      </div>
    </main>
  );
}

export function ContactPage() {
  return (
    <main id="main">
      <Meta
        title="Contact | Beautify Ourselves"
        description="Customer care information for Beautify Ourselves."
        path="/pages/contact.html"
        noIndex
      />
      <PageHero
        eyebrow="Customer care"
        title="We’ll be here to help."
        lead="Customer care will be available when the Beautify Ourselves collection opens for ordering."
      />
      <div className="content-page">
        <p>No orders are currently available. Customer care details will appear here before the shop opens.</p>
        <Link className="text-link" to={routes.shop}>
          Explore the coming collection <span aria-hidden="true">→</span>
        </Link>
      </div>
    </main>
  );
}

export function PrivacyPage() {
  return (
    <main id="main">
      <Meta
        title="Privacy Information | Beautify Ourselves"
        description="Privacy information for Beautify Ourselves."
        path="/pages/privacy.html"
        noIndex
      />
      <PageHero
        eyebrow="Privacy"
        title="Privacy information"
        lead="The Beautify Ourselves shop is not currently open for orders."
      />
      <article className="content-page">
        <div className="notice">
          <strong>Privacy policy not yet published</strong>
          <p>
            A complete privacy policy will be available before the shop opens or personal
            information is collected through the website.
          </p>
        </div>
        <p>This page is not a final privacy policy.</p>
      </article>
    </main>
  );
}

export function TermsPage() {
  return (
    <main id="main">
      <Meta
        title="Terms | Beautify Ourselves"
        description="Terms information for Beautify Ourselves."
        path="/pages/terms.html"
        noIndex
      />
      <PageHero
        eyebrow="Terms"
        title="Shopping terms"
        lead="The Beautify Ourselves shop is not currently open for orders."
      />
      <article className="content-page">
        <div className="notice">
          <strong>Shopping terms not yet published</strong>
          <p>Complete terms of sale will be available before ordering opens.</p>
        </div>
        <p>This page is not a final set of terms.</p>
      </article>
    </main>
  );
}

export function NotFoundPage() {
  return (
    <main id="main" className="content-page">
      <Meta
        title="Page not found | Beautify Ourselves"
        description="The requested Beautify Ourselves page could not be found."
        path="/404.html"
        noIndex
      />
      <div className="empty-state">
        <p className="eyebrow">Page not found</p>
        <h1>Let’s get you back to the collection.</h1>
        <Link className="button" to={routes.shop}>
          Explore categories
        </Link>
      </div>
    </main>
  );
}
